/**
 * FiveSHub — 5S category accordion on the athlete-side /programs page.
 *
 * Renders the five training pillars in canonical order (Speed → Strength →
 * Stamina → Suppleness → Skill) as color-coded cards. Each S shows a
 * LIVE / COMING SOON pill based on whether programs are assigned to the
 * athlete in that bucket. Clicking a LIVE card expands it inline to
 * reveal the actual program cards (rendered by the parent via the
 * `renderProgram` prop so this file stays presentational).
 *
 * Order + copy taken from the owner's reference layout so MaxSC matches
 * the mental model parents already see on his other training app.
 */
import { useState, useMemo } from "react";
import { Zap, Dumbbell, Flame, Wind, Target, Lock, ChevronRight, ChevronDown } from "lucide-react";

// Canonical pillars, in the order they render. Tone classes pair a
// border + bg-tint + accent-text so each card has its own personality
// without going full rainbow.
const PILLARS = [
    {
        key: "speed",
        title: "Speed",
        icon: Zap,
        subtitle: "Accelerate · Decelerate · Change Direction",
        description: "Sprint mechanics, plyometrics, reactive power. Move faster than the moment.",
        tone: {
            border: "border-amber-700/50",
            bgCard: "bg-amber-950/20",
            bgIcon: "bg-amber-950/40 border-amber-700/50",
            text: "text-amber-300",
            accent: "text-amber-400",
            ring: "hover:border-amber-500",
        },
    },
    {
        key: "strength",
        title: "Strength",
        icon: Dumbbell,
        subtitle: "Build the engine that powers everything",
        description: "Progressive overload programs from Foundations to Advanced. The full library is open.",
        tone: {
            border: "border-sky-700/50",
            bgCard: "bg-sky-950/20",
            bgIcon: "bg-sky-950/40 border-sky-700/50",
            text: "text-sky-300",
            accent: "text-sky-400",
            ring: "hover:border-sky-500",
        },
    },
    {
        key: "stamina",
        title: "Stamina",
        icon: Flame,
        subtitle: "Conditioning · Energy Systems · Work Capacity",
        description: "Aerobic base, lactate threshold, sport-specific endurance protocols.",
        tone: {
            border: "border-rose-700/50",
            bgCard: "bg-rose-950/20",
            bgIcon: "bg-rose-950/40 border-rose-700/50",
            text: "text-rose-300",
            accent: "text-rose-400",
            ring: "hover:border-rose-500",
        },
    },
    {
        key: "suppleness",
        title: "Suppleness",
        icon: Wind,
        subtitle: "Mobility · Flexibility · Recovery",
        description: "Joint health, soft-tissue work, structured recovery sessions to keep you durable.",
        tone: {
            border: "border-emerald-700/50",
            bgCard: "bg-emerald-950/20",
            bgIcon: "bg-emerald-950/40 border-emerald-700/50",
            text: "text-emerald-300",
            accent: "text-emerald-400",
            ring: "hover:border-emerald-500",
        },
    },
    {
        key: "skill",
        title: "Skill",
        icon: Target,
        subtitle: "Sport IQ · Movement quality · Coordination",
        description: "Position-specific drills, ball/stick handling, fine-motor and decision-making work.",
        tone: {
            border: "border-violet-700/50",
            bgCard: "bg-violet-950/20",
            bgIcon: "bg-violet-950/40 border-violet-700/50",
            text: "text-violet-300",
            accent: "text-violet-400",
            ring: "hover:border-violet-500",
        },
    },
];


export default function FiveSHub({ programs, renderProgram }) {
    // Group programs by 5S category. Unknown / extras / null land in
    // an "other" bucket that we display at the bottom only if non-empty.
    const groups = useMemo(() => {
        const out = { speed: [], strength: [], stamina: [], suppleness: [], skill: [], other: [] };
        for (const p of programs || []) {
            const cat = (p.category || "").toLowerCase();
            if (cat in out && cat !== "other") out[cat].push(p);
            else out.other.push(p);
        }
        return out;
    }, [programs]);

    // Auto-open the first LIVE pillar so parents don't have to click first
    const firstLive = PILLARS.find((p) => groups[p.key].length > 0)?.key;
    const [openKey, setOpenKey] = useState(firstLive);

    return (
        <div className="space-y-4">
            {PILLARS.map((pillar) => {
                const list = groups[pillar.key];
                const isLive = list.length > 0;
                const isOpen = openKey === pillar.key && isLive;
                const Icon = pillar.icon;
                return (
                    <article
                        key={pillar.key}
                        data-testid={`pillar-${pillar.key}`}
                        data-live={isLive ? "true" : "false"}
                        className={`border rounded-2xl overflow-hidden transition-colors ${pillar.tone.border} ${pillar.tone.bgCard} ${isLive ? `cursor-pointer ${pillar.tone.ring}` : "opacity-70"}`}
                    >
                        <button
                            type="button"
                            data-testid={`pillar-toggle-${pillar.key}`}
                            disabled={!isLive}
                            onClick={() => setOpenKey(isOpen ? null : pillar.key)}
                            className="w-full px-4 sm:px-6 py-5 text-left flex items-center gap-4"
                        >
                            <div className={`w-14 h-14 rounded-xl border flex items-center justify-center shrink-0 ${pillar.tone.bgIcon}`}>
                                <Icon className={`w-7 h-7 ${pillar.tone.accent}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-display text-2xl sm:text-3xl tracking-wider text-white">
                                        {pillar.title}
                                    </h3>
                                    {isLive ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 border border-emerald-600/50 text-emerald-300 bg-emerald-900/20 rounded-full">
                                            Live · {list.length}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 border border-zinc-700 text-zinc-400 rounded-full">
                                            <Lock className="w-2.5 h-2.5" /> Coming soon
                                        </span>
                                    )}
                                </div>
                                <div className={`mt-1 text-[12px] sm:text-sm font-semibold ${pillar.tone.text}`}>
                                    {pillar.subtitle}
                                </div>
                                <p className="mt-1.5 text-[12px] sm:text-sm text-zinc-400 leading-relaxed">
                                    {pillar.description}
                                </p>
                            </div>
                            {isLive && (
                                <div className="shrink-0 text-zinc-400">
                                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                </div>
                            )}
                        </button>

                        {isOpen && (
                            <div data-testid={`pillar-body-${pillar.key}`} className="px-4 sm:px-6 pb-5 pt-1 space-y-3">
                                {list.map((p) => renderProgram(p))}
                            </div>
                        )}
                    </article>
                );
            })}

            {/* Uncategorized programs — only render if any exist */}
            {groups.other.length > 0 && (
                <article data-testid="pillar-other" className="border border-zinc-800 rounded-2xl bg-zinc-950/60">
                    <div className="px-4 sm:px-6 py-4 border-b border-zinc-800">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">Other</div>
                        <p className="text-[12px] text-zinc-500 mt-0.5">
                            Programs your coach assigned that aren't yet pinned to one of the 5 Ss.
                        </p>
                    </div>
                    <div className="px-4 sm:px-6 py-4 space-y-3">
                        {groups.other.map((p) => renderProgram(p))}
                    </div>
                </article>
            )}
        </div>
    );
}
